import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  employeeApiRequest,
  getEmployeeToken,
  setEmployeeSession,
} from '../utils/employeeApi';
import loginLogo from '../assets/images/login-logo.png';
import './EmployeeMobileLogin.css';

const EyeIcon = () => (
  <svg
    className="employee-mobile-login__icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3.5" fill="currentColor" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    className="employee-mobile-login__icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      d="M3 3 21 21"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M10.73 6.1A9.66 9.66 0 0 1 12 6c7 0 11 6 11 6a18.46 18.46 0 0 1-3.07 3.56"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.36 6.64A18.27 18.27 0 0 0 1 12s4 7 11 7a9.9 9.9 0 0 0 3.55-.65"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EmployeeMobileLogin = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = getEmployeeToken();
    if (token) {
      navigate('/employee/mobile/details', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedIdentifier = identifier.trim();
    const trimmedPassword = password.trim();

    if (!trimmedIdentifier || !trimmedPassword) {
      setError('Please enter both your mobile number or username and password.');
      return;
    }

    const normalizedIdentifier = /^\d{10}$/.test(trimmedIdentifier)
      ? `${trimmedIdentifier}`
      : trimmedIdentifier;

    setError('');
    setIsSubmitting(true);

    console.log('Submitting employee mobile login', {
      identifier: normalizedIdentifier,
      passwordProvided: Boolean(trimmedPassword),
    });

    try {
      const response = await employeeApiRequest('/api/public/employee/login', {
        method: 'POST',
        body: {
          identifier: normalizedIdentifier,
          password: trimmedPassword,
        },
      });

      const { token, employee } = response.data ?? {};
      if (!token) {
        throw new Error('Login token was not provided by the server.');
      }

      setEmployeeSession(token, employee ?? null);
      navigate('/employee/mobile/details', { replace: true });
    } catch (err) {
      setError(err.message || 'Unable to login. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="employee-mobile-login">
      <header className="employee-mobile-login__header">
        <div className="employee-mobile-login__intro">
          <p className="employee-mobile-login__greeting">Hello</p>
          <h1 id="employee-mobile-login-title" className="employee-mobile-login__title">
            Sign in!
          </h1>
        </div>
      </header>

      <main className="employee-mobile-login__main" aria-labelledby="employee-mobile-login-title">
        <section className="employee-mobile-login__content">
          <div className="employee-mobile-login__logo">
            <img src={loginLogo} alt="Company logo" />
          </div>

          {error && (
            <div className="employee-mobile-login__alert" role="alert">
              {error}
            </div>
          )}

          <form className="employee-mobile-login__form" onSubmit={handleSubmit} noValidate>
            <label className="employee-mobile-login__field" htmlFor="employee-identifier">
              <span className="employee-mobile-login__label">Mobile number or username</span>
              <div className="employee-mobile-login__input-group">
                <span className="employee-mobile-login__prefix" aria-hidden="true">
                  +91
                </span>
                <input
                  id="employee-identifier"
                  type="text"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="employee-mobile-login__input"
                  autoComplete="username"
                  placeholder="Enter registered mobile number"
                  required
                />
              </div>
              <small className="employee-mobile-login__hint">
                You can also sign in using your employee ID or username.
              </small>
            </label>

            <label className="employee-mobile-login__field" htmlFor="employee-password">
              <span className="employee-mobile-login__label">Password</span>
              <div className="employee-mobile-login__input-group">
                <input
                  id="employee-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="employee-mobile-login__input"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="employee-mobile-login__toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </label>

            <div className="employee-mobile-login__links">
              <Link to="/employee/forgot-password" className="employee-mobile-login__forgot">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              className="employee-mobile-login__submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in…' : 'Login'}
            </button>
          </form>
        </section>
      </main>
    </div>
  );
};

export default EmployeeMobileLogin;
